import React, { useEffect, useState, useMemo } from 'react';
import classNames                              from 'classnames';
import Helmet                                  from 'react-helmet';
import { Link, useHistory }                    from 'react-router-dom';
import { useTranslation }                      from 'react-i18next';

import useRequest          from '../../../../common/services/request/request.hook';
import CatalogGrid         from '../../../../components/catalog/CatalogGrid';
import ClearBookmarksModal from '../../../../components/ClearBookmarksModal';
import Preloader           from '../../../../components/containers/preloader';
import ClearBookmarks      from '../../../../components/buttons/ClearBookmarks';
import ContactUs           from '../../../../components/buttons/ContactUs';
import Share               from '../../../../components/buttons/Share';
import PageHeader          from '../../../../components/pageHeader';
import { useBookmarks }    from '../../../../providers/bookmarks';
import { useFavorites }    from '../../../../providers/favorites';
import { useShare }        from '../../../../providers/share';
import { useUser }         from '../../../../providers/user';

const Bookmarks = function() {
  const { t }                           = useTranslation();
  const { bookmarks }                   = useUser();
  const {
          addLink,
          loading,
          closeLoading,
        }                               = useShare();
  const { getFavorites }                = useFavorites();
  const request                         = useRequest();
  const [currentItems, setCurrentItems] = useState([]);
  const [isShare, setIsShare]           = useState(false);
  const { location: { pathname } }      = useHistory();
  const { showModal }                   = useBookmarks();

  useEffect(() => {
    if (Array.isArray(bookmarks) && !bookmarks.length && !isShare) {
      showModal();
    }
  }, [bookmarks, isShare]);

  React.useEffect(() => {
    scrollTo({
      top:  0,
      left: 0,
    });
  }, []);

  const [page, setPage] = React.useState(1);

  const showMore = React.useCallback(() => {
    setPage((prevPage) => ++prevPage);
  }, [page, setPage]);

  const meta = {
    title:       `${t('client_bookmarks')} - ${t('steelline')}`,
    keywords:    t('client_bookmarks'),
    description: t('client_bookmarks'),
  };

  const count = React.useMemo(
    () =>
      Object.keys(bookmarks)
        .reduce(
          (count, itemKey) =>
            bookmarks.hasOwnProperty(itemKey)
              ? count + Object.keys(bookmarks[itemKey]).length
              : count,
          0,
        ),
    [bookmarks],
  );

  const validateBookmarks = React.useCallback(
    (bookmarks) => {
      return Object.values(bookmarks)
        .flatMap((obj) => Object.entries(obj))
        .map(([key, value]) => (
          {
            [value.common.offerId]: value,
          }));
    },
    [page, bookmarks],
  );

  useEffect(() => {
    const favorites        = getFavorites();
    const isValidFavorites = validateBookmarks(favorites).length > 0;
    const isFavoritePage   = typeof window !== 'undefined' && window.location.pathname.endsWith('/izbrannoe');

    if (isValidFavorites && !isFavoritePage) {
      setIsShare(true);
      setCurrentItems(validateBookmarks(favorites));
    } else {
      setIsShare(false);
      setCurrentItems(validateBookmarks(bookmarks));
    }
  }, [bookmarks, isShare, pathname]);

  const currentCount = React.useMemo(
    () =>
      currentItems.reduce((count, item) => count + Object.keys(item).length, 0),
    [currentItems],
  );

  const offerIds = useMemo(() => {
    return Object.values(bookmarks)
      .flatMap((model) =>
        Object.keys(model)
          .map((id) => id.replace(/_/gi, '')),
      );
  }, [bookmarks]);

  const getLink = React.useCallback(async() => {
    if (offerIds.length > 0) {
      const response = await request.post('/set-favorites-link', {
        id: offerIds,
      });
      addLink(response.link);
    }
    closeLoading();
  }, [offerIds, request, addLink, closeLoading]);

  return (
    <>
      <div className={'page-content'}>
        <Helmet>
          <title>{meta.title}</title>
          <meta name={'description'} content={meta.description} />
          <meta name={'keywords'} content={meta.keywords} />
        </Helmet>
        <PageHeader
          controlPanel={
            <div className={'bookmarks_control_panel'}>
              {!isShare && !!currentItems.length && <ClearBookmarks />}
            </div>
          }
          headerClass={'bookmarks_header'}
          headerTitleClass={'bookmarks_title'}
          noBorder
        >
          {isShare ? t('client_share_bookmarks') : t('client_bookmarks')}
        </PageHeader>

        <main className={'bookmarks'}>
          <CatalogGrid
            items={currentItems}
            count={0}
            countModels={0}
            label={''}
            showMore={count !== currentCount}
            showMoreFunc={showMore}
            shareButtonVisible
          />
          {!currentItems.length && (
            <p className={'bookmarks_empty'}>{t('client_bookmarks_empty_goods')}{' '}
              <Link to={'/vhodnye-dveri'}>{t('client_in_catalog')}</Link>
            </p>
          )}
          {!isShare && (<div className={'control_buttons'}>
            {!!currentItems.length && <Share getLink={getLink} />}
            <Link to={'/vhodnye-dveri'} className={classNames('btn', 'primary')}>{t('label_goToCatalog')}</Link>
            <ContactUs label={t('btn_get_consultation')} />
          </div>)}
        </main>
      </div>
      <ClearBookmarksModal />
      {loading && <Preloader />}
    </>
  );
};

export default Bookmarks;
